import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ErpatApiError, type TokenResponse } from '@erpat/sdk';
import {
  erpatClient,
  rememberCompanyKey,
  clearSession,
  hasAccessToken,
  getSessionEpoch,
  getStoredCompanyKey,
  cancelSessionRequests,
} from '@/services/api/erpat-client';
import { describeAuthError } from '@/services/api/http';
import { startTokenAutoRefresh, stopTokenAutoRefresh } from '@/services/api/token-refresh';
import { startPresenceHeartbeat, stopPresenceHeartbeat } from '@/services/api/presence-heartbeat';
import {
  startEventReminders,
  stopEventReminders,
  clearEventReminders,
} from '@/services/event-reminders';
import { stopRealtime } from '@/services/realtime.service';
import { clearPendingAction } from '@/services/realtime-navigation';
import { closeFileViewer } from '@/services/file-viewer';
import { notificationsDevice } from '@/services/device/notifications.service';
import { clearSessionScope, getSessionScope, setSessionScope } from '@/services/session-scope';
import { deviceLoginFields } from '@/utils/device';
import { purgeUserCaches } from '@/services/offline-cache';
import { resetBrandColor } from '@/theme/brand-color';
import { useUserStore } from '@/stores/user.store';
import { useMessagesStore } from '@/stores/messages.store';
import { useNotificationsStore } from '@/stores/notifications.store';
import { useAvatarsStore } from '@/stores/avatars.store';
import { useApprovalsStore } from '@/stores/approvals.store';
import { useSecurityStore } from '@/stores/security.store';
import { useCutoffStore } from '@/stores/cutoff.store';
import { clearPlannedToday } from '@/utils/todo-planned-today';
import { clearAllLocalTaskFields } from '@/utils/todo-local-fields';
import { clearNoteEdits } from '@/utils/note-edits';
import { useSurveysStore } from '@/stores/surveys.store';
import { clearAnalyticsUser, identifyAnalyticsUser, trackEvent } from '@/services/analytics';
import { forgetRealtimeLogsAvailability } from '@/services/realtime-logs';
import { startOfflineAttendance, stopOfflineAttendance } from '@/services/offline/offline-attendance';
import { useAttendanceStore } from '@/stores/attendance.store';
import { useSyncStore } from '@/stores/sync.store';

export type SessionStatus = 'unknown' | 'authenticated' | 'guest';

/**
 * How long sign-out waits for the server to confirm `POST /auth/logout` before
 * signing out locally anyway. Matches the push revoke's bound (DEC-090): long
 * enough for a healthy server, short enough that a busy one cannot keep the
 * user staring at "Signing Out…".
 */
export const SERVER_LOGOUT_TIMEOUT_MS = 3_000;

/**
 * How a sign-in leg ENDED.
 *
 * Exhaustive by design. The sign-in page raises a full-screen "authenticating"
 * overlay before calling {@link useSessionStore.login} / `completeMfa` and takes
 * it down on the result, so every path through those two — including the ones
 * that used to throw — has to arrive here as exactly one of four values:
 *
 *  - `authenticated` — tokens are cached and the router guard will admit the
 *    user. The ONLY value that may be followed by a navigation into the app.
 *  - `mfa_required`  — credentials accepted, a one-time code is owed. The store
 *    has already flipped `mfaPending`; the page just switches legs. Not an error.
 *  - `abandoned`     — the attempt produced nothing and the user must NOT see an
 *    error, because they caused it: a Cancel, a sign-out, or a second submit
 *    landing while one is still running. Anything the server eventually answered
 *    has been discarded and any tokens it issued cleared.
 *  - `failed`        — everything else, already rendered as user-facing copy.
 *    The STORE owns the wording, not the page: only the store knows how long the
 *    request ran, and that is what separates "the server took too long" from
 *    "you are offline" (see `describeAuthError()`).
 *
 * Neither function throws. That is the point: an overlay that must come down on
 * every terminal outcome should not depend on a caller remembering a `finally`.
 */
export type SignInOutcome =
  | { status: 'authenticated' }
  | { status: 'mfa_required' }
  | { status: 'abandoned' }
  | { status: 'failed'; message: string };

/**
 * Authentication / session state.
 *
 * Tokens themselves live in the SDK client (persisted via its `localStorage`
 * adapter). This store tracks the *derived* session status for the router
 * guard and exposes the login → MFA → logout flow used by SignInPage.
 */
export const useSessionStore = defineStore('session', () => {
  const status = ref<SessionStatus>('unknown');
  const mfaToken = ref<string | null>(null);
  /**
   * True from the moment the server challenges for a one-time code until that
   * challenge is answered or dropped — i.e. which leg the sign-in form is on.
   *
   * Deliberately NOT derived from `mfaToken`. A tenant may challenge WITHOUT
   * issuing one (`mfa_required: true` with no `mfa_token`; `auth.mfa()` accepts
   * the code alone), and the derived version then reported "no challenge" while
   * the page was showing the code field — two sources of truth, disagreeing.
   * THIS is the source of truth; the page renders it and holds no flag of its own.
   */
  const mfaPending = ref(false);

  const isAuthenticated = computed(() => status.value === 'authenticated');

  /**
   * One sign-in leg at a time.
   *
   * NOT a single-flight like `user.store.refresh()`: sharing the first attempt's
   * promise would be a lie here, because a second submit may carry different
   * credentials. A concurrent call is REFUSED and reported as `abandoned`, so
   * nothing is rendered for it.
   *
   * `signInAttempt` is bumped by every start AND by `abandonSignIn()`, so an
   * attempt whose id no longer matches has been superseded and must not release
   * the guard on its way out — otherwise a cancelled-then-retried sign-in would
   * have its replacement un-guarded by the corpse of the first.
   */
  let signInAttempt = 0;
  let signInInFlight = false;

  /**
   * Reflect a freshly issued token pair (or an MFA challenge) into state.
   * `method` names the step that actually issued the tokens — it is only
   * reported to analytics, as the `login` event's method.
   */
  function applyTokenResponse(
    data: TokenResponse | undefined,
    method: 'password' | 'mfa',
  ): SignInOutcome {
    if (data?.mfa_required) {
      // The challenge token is optional; the LEG is not (see `mfaPending`).
      mfaToken.value = (data.mfa_token as string | undefined) ?? null;
      mfaPending.value = true;
      return { status: 'mfa_required' };
    }

    // A token-less "success". The SDK skips caching when the payload carries no
    // `access_token` (`cacheTokensFromResponse`: `if (!token || !token.access_token) return`),
    // so `ok: true` on its own proves nothing. Flipping `status` here made the
    // store claim a session the ROUTER GUARD cannot see — the guard reads
    // `hasAccessToken()`, not this store — so the caller navigated to Home, the
    // guard found no token, parked the route and bounced straight back to
    // sign-in: no error shown, and a store permanently insisting the user was
    // signed in. Ask the guard's own question, and refuse out loud.
    //
    // A plain Error, not an ErpatApiError: the server did not fail, it answered
    // 2xx — this is OUR invariant breaking. `errorMessage()` renders a plain
    // Error's message verbatim, so the text below is what the user reads; an
    // ErpatApiError with status 0 would have been mistranslated into
    // "No connection."
    if (!hasAccessToken()) {
      throw new Error(
        'Sign-in did not complete. Please try again, or contact your administrator if it continues.',
      );
    }

    resetMfa();
    // Auth boundary: drop any cached API reads BEFORE the first authenticated
    // fetch. Cached `/v1/api/me/*` URLs are identical for every user and every
    // tenant, so on a shared device this is what stops the previous session's
    // data being served to this one. Unconditional by design — after
    // clearSession() there is no previous identity left to compare against, and
    // the cost is one cold cache per sign-in.
    purgeUserCaches();
    // The session identity snapshot (phase6-contract.md §3.1): captured while
    // the token is readable, so every identity-keyed clear at sign-out — the
    // cursor, the outbox scopes, the parked action, the push record — works on
    // the EXPIRY path too, where the tokens are gone before signOutLocal() runs.
    setSessionScope({});
    status.value = 'authenticated';

    // Past this line the session EXISTS: the token is cached, the guard will
    // admit the user, and the caller is about to navigate. Everything below only
    // FURNISHES that session — a refresher, a heartbeat, reminders, an identity
    // fetch, the Security PIN state, analytics — and not one of them is a reason
    // to fail a sign-in that has already succeeded. All seven are individually
    // throw-safe today; the fence is what keeps that true when the eighth is
    // added, because a throw here would leave the store authenticated, the page
    // showing an error, and the user stranded on sign-in with a live session.
    try {
      // Keep the freshly issued token alive in the background from now on.
      startTokenAutoRefresh();
      // Report presence while the app is in the foreground, so a user reading a screen without
      // making requests still shows as online to their colleagues.
      startPresenceHeartbeat();
      // Plan event reminders for this account — the banner, and (on Android) the
      // device notification that fires while the app is closed.
      startEventReminders();
      // Pull the user's identity (name, department, …) for the freshly issued token.
      void useUserStore().refresh();
      // And this account's Security PIN state, so the first confidential prompt
      // already knows whether to ask for the PIN or the password. Never rejects.
      void useSecurityStore().refresh();
      // Analytics: pseudonymous identity first, THEN the sign-in event, so the
      // event is attributed to the user (the queue keeps that order).
      identifyAnalyticsUser();
      trackEvent('login', { method });
      // Offline attendance for this account: its own queued punches resume, the
      // device's state is published, provisioning runs in the background.
      // Never rejects.
      void startOfflineAttendance();
    } catch (err) {
      console.warn('[session] post-sign-in setup failed', err);
    }

    return { status: 'authenticated' };
  }

  /**
   * Step 1 — set the tenant and authenticate with email + password.
   * Returns `{ mfaRequired }`; when true, call {@link completeMfa}.
   */
  async function login(
    companyKey: string,
    email: string,
    password: string,
    remember = true,
  ): Promise<SignInOutcome> {
    if (signInInFlight) return { status: 'abandoned' };
    signInInFlight = true;
    const attempt = ++signInAttempt;

    // The session generation this attempt belongs to. Captured BEFORE the await,
    // exactly as every other async consumer does (`refreshSession()`,
    // `user.store.refresh()`, `presence-heartbeat.beat()`,
    // `identifyAnalyticsUser()`): a Cancel or a sign-out landing mid-flight bumps
    // it, and the check after the await is what stops applyTokenResponse()
    // quietly UNDOING that teardown. The SDK caches — and with `rememberLogin`
    // PERSISTS — the issued pair inside `auth.login()` before our code runs, so
    // by then a cancelled-but-successful login has already written a restorable
    // session to localStorage. Only the re-clear below takes it back.
    const epoch = getSessionEpoch();

    // The tenant has to be set BEFORE the request: the server resolves the company
    // from `X-Company-Key` before authentication runs, so it cannot wait for a
    // result. Snapshot what it replaced — a failed attempt (a typo'd key, the
    // right password against the wrong tenant) must not leave the app pointed at
    // a company the user never signed in to. That key is read by `auth.refresh()`,
    // by `tenantStamp()` — which decides whose realtime cursor, outbox scope and
    // parked deep link are whose — and by any OTHER TAB still holding a live
    // session, which re-reads it from storage on every call.
    const previousCompanyKey = getStoredCompanyKey();
    const startedAt = Date.now();
    rememberCompanyKey(companyKey);

    try {
      const res = await erpatClient.auth.login(
        { email, password, ...deviceLoginFields() },
        { rememberLogin: remember },
      );
      if (getSessionEpoch() !== epoch) return discardAbandoned();
      return applyTokenResponse(res.data, 'password');
    } catch (err) {
      // Any failure rolls the tenant back; the next successful attempt re-sets it.
      // Skipped when there was nothing to roll back to (a fresh install), where a
      // stray key can corrupt nothing — there are no artefacts yet.
      if (previousCompanyKey !== undefined) rememberCompanyKey(previousCompanyKey);
      return { status: 'failed', message: describeAuthError(err, Date.now() - startedAt) };
    } finally {
      // Released only by the attempt that still OWNS the flow. A Cancel already
      // released it and bumped the id, and the user may have started a
      // replacement since — clearing the flag here would let that replacement run
      // beside a third submit.
      if (signInAttempt === attempt) signInInFlight = false;
    }
  }

  /** Step 2 (only when MFA is enforced) — submit the one-time code. */
  async function completeMfa(code: string, remember = true): Promise<SignInOutcome> {
    if (signInInFlight) return { status: 'abandoned' };
    signInInFlight = true;
    const attempt = ++signInAttempt;
    const epoch = getSessionEpoch();
    const startedAt = Date.now();
    try {
      // The MFA step is what actually issues tokens when MFA is enforced, so the
      // device metadata rides this call too (not just the first login leg).
      const res = await erpatClient.auth.mfa(
        { code, ...(mfaToken.value ? { mfa_token: mfaToken.value } : {}), ...deviceLoginFields() },
        { rememberLogin: remember },
      );
      if (getSessionEpoch() !== epoch) return discardAbandoned();
      // May legitimately answer `mfa_required` AGAIN — a rotated challenge, or a
      // second factor. The caller used to ignore what came back and navigate
      // regardless, which is the same token-less dead end as a missing
      // access_token; now it is a value the caller has to handle.
      return applyTokenResponse(res.data, 'mfa');
    } catch (err) {
      return { status: 'failed', message: describeAuthError(err, Date.now() - startedAt) };
    } finally {
      if (signInAttempt === attempt) signInInFlight = false;
    }
  }

  /**
   * Teardown won the race — the rule `refreshSession()` applies to a rotation.
   *
   * The tokens the SDK just cached belong to a session the user walked away from,
   * so they go. The `status` guard is load-bearing: cancel → retry → the RETRY
   * succeeds → the first attempt finally lands 30 s later and finds the epoch
   * moved. Clearing unconditionally there would sign out a user who is already
   * inside the app. (The first attempt's own `cacheTokensFromResponse` has by then
   * clobbered the second's pair with its own — both are valid pairs for the same
   * user, so that part is benign and unavoidable without patching the SDK.)
   */
  function discardAbandoned(): SignInOutcome {
    if (status.value !== 'authenticated') clearSession();
    return { status: 'abandoned' };
  }

  /**
   * Drop an MFA challenge and go back to the password leg.
   *
   * The challenge token is single-use and belongs to ONE credential + tenant pair,
   * so it must never survive into the next attempt: a user who backs out to
   * correct their email, or to switch company, would otherwise present a challenge
   * the server has no reason to honour. No tokens exist yet at this point in the
   * flow, so nothing else is touched.
   */
  function resetMfa(): void {
    mfaToken.value = null;
    mfaPending.value = false;
  }

  /**
   * Abandon an in-flight sign-in — the overlay's Cancel.
   *
   * Bumping the session generation is what makes it stick: the running attempt
   * re-checks it after its await and discards whatever the server answered,
   * including a SUCCESS whose tokens the SDK has already cached and persisted.
   * `clearSession()` also takes back anything already written, so a cancel can
   * never leave a token behind for the next boot to restore.
   *
   * Releasing the guard here rather than waiting for the attempt to settle is
   * deliberate: `auth.login()` is not cancellable (`AuthRequestOptions` carries no
   * `signal`, and the SDK clears its own timeout the moment response HEADERS
   * arrive, so a stalled body hangs forever). Waiting would lock the user out of
   * retrying for as long as the request hangs — precisely the case a Cancel button
   * exists for.
   */
  function abandonSignIn(): void {
    signInAttempt++;
    signInInFlight = false;
    resetMfa();
    clearSession();
  }

  /**
   * Restore session on app boot. The SDK auto-loads any persisted token; if one
   * exists we optimistically treat the session as authenticated (a 401 on the
   * first real call triggers refresh / sign-out via the http layer).
   */
  function restore(): void {
    if (hasAccessToken()) {
      // Same snapshot as applyTokenResponse(): the restored token is readable now.
      setSessionScope({});
      status.value = 'authenticated';
      startTokenAutoRefresh();
      startPresenceHeartbeat();
      // Re-arm reminders from the plan already on disk, then refresh it.
      startEventReminders();
      // Hydrate the cached identity for the restored session.
      void useUserStore().refresh();
      // Same for the Security PIN state (PIN vs password on confidential prompts).
      void useSecurityStore().refresh();
      // Re-attach the pseudonymous analytics identity (no `login` event — the
      // user did not sign in, the app merely reopened).
      identifyAnalyticsUser();
      // Offline attendance from what the device holds — works with no network,
      // and resumes this account's queued punches. Never rejects.
      void startOfflineAttendance();
    } else {
      status.value = 'guest';
    }
  }

  /**
   * Full sign-out: best-effort server revoke, then clear local state.
   *
   * Order (phase6-contract.md §3.1 "Sign-out order"): revoke this
   * installation's push endpoint (bounded 3 s inside the facade — DEC-090,
   * the End-User logout does not revoke it) → stop the socket AND forget the
   * persisted cursor (`forget: true`, DEC-083) → the server logout → the local
   * clear. The endpoint DELETE and the socket close both need the token that
   * `auth.logout()` is about to revoke, so they go first.
   *
   * Nothing the session still has running may hold it up, and nothing may stop
   * it: whatever is in flight is cancelled first, every server step is bounded,
   * and the local clear runs whatever happens before it. Worst case — a server
   * that answers nothing — is the push revoke's 3 s plus
   * {@link SERVER_LOGOUT_TIMEOUT_MS}. Never rejects.
   */
  async function logout(): Promise<void> {
    try {
      // Stop the background refresher *before* the revoke round-trip so an interval
      // or foreground tick can't fire a competing token rotation mid-logout. The
      // redundant stop in signOutLocal() is idempotent.
      stopTokenAutoRefresh();
      // Same reasoning: no heartbeat may fire against a token we are about to revoke.
      stopPresenceHeartbeat();
      // And no reminder refresh may fetch the calendar mid-revoke.
      stopEventReminders();
      // Cancel everything this session still has in flight — a page load, an
      // upload, a message send, an assistant reply, a token refresh. They would
      // otherwise hold the connections the two requests below need (a browser
      // opens only a few per server), and could land after the local clear. The
      // requests sign-out sends next are issued after this, so they are unaffected.
      cancelSessionRequests();
      // Revoke the host-push endpoint while the bearer is still valid. Best
      // effort: the facade bounds it and never throws past this line.
      try {
        await notificationsDevice.revoke();
      } catch {
        /* a failed revoke never blocks the sign-out — the local records go below */
      }
      // Close the socket before the revoke: the server answers a logout by closing
      // every socket of this user with 4009, and a live client would otherwise
      // schedule a re-bootstrap against the token we are about to revoke. This
      // is a sign-out, so the persisted cursor goes with it.
      stopRealtime({ forget: true });
      // Raised BEFORE signOutLocal() detaches the analytics identity, so the
      // event is still the user's. Expiry does not reach here — that is the
      // http layer's sign-out, not something the person did.
      trackEvent('sign_out');
      await revokeServerSession();
    } catch (err) {
      // Sign-out must never fail: a step above throwing would otherwise leave the
      // tokens in place and the router guard would walk the user straight back in.
      console.warn('[session] sign-out step failed; signing out locally anyway', err);
    } finally {
      signOutLocal();
    }
  }

  /**
   * `POST /v1/api/auth/logout`, waited on for at most
   * {@link SERVER_LOGOUT_TIMEOUT_MS}; never throws.
   *
   * The request is ABORTED at the deadline, not left to finish in the background
   * the way the push DELETE is (DEC-090): `auth.logout()` clears the SDK's tokens
   * when it succeeds, so a late success would sign out a user who had already
   * signed back in. The cost of the abort is a refresh token the server never
   * heard was revoked — the device has already forgotten it, and it expires on
   * its own.
   */
  async function revokeServerSession(): Promise<void> {
    const deadline = new AbortController();
    const timer = setTimeout(() => deadline.abort(), SERVER_LOGOUT_TIMEOUT_MS);
    try {
      await erpatClient.auth.logout(undefined, deadline.signal);
    } catch (err) {
      // Expected and non-fatal: offline, a 401 on an expired bearer, or the
      // deadline (an ErpatApiError before the headers, a bare AbortError after).
      const expected =
        err instanceof ErpatApiError || (err instanceof Error && err.name === 'AbortError');
      if (!expected) console.warn('[session] logout request failed', err);
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Clear tokens + state without calling the server (used on expiry, and the
   * tail of {@link logout}).
   *
   * Order (phase6-contract.md §3.1): `stopRealtime({forget: true})` → the
   * outbox scopes → the parked navigation action → the local push records →
   * the existing clears → `clearSession()` → the session snapshot LAST. Every
   * identity-keyed clear reads the session identity SNAPSHOT
   * (`getSessionScope()`), never the token: on the expiry path
   * `handleSessionExpired()` has already cleared the tokens before this runs.
   * A company switch is a sign-out and flows through here too.
   */
  function signOutLocal(): void {
    stopTokenAutoRefresh();
    // Cancel whatever this session still has in flight. logout() already did,
    // but this is also the expiry and company-switch path, and a request started
    // while logout() waited on the server would otherwise outlive the session.
    // Their rejections arrive after this function returns, when the epoch bumped
    // by clearSession() below makes every epoch-guarded consumer discard them.
    cancelSessionRequests();
    // A document still open full-screen (a medical certificate, a payslip) must
    // not stay over the sign-in page, nor keep its re-sign hook for the next
    // account. Idempotent when nothing is open.
    closeFileViewer();
    // Offline attendance stops HERE, synchronously, while the tokens still exist:
    // the sync engine stops, nothing it had in flight can settle for the old
    // scope, every wake detaches. Its IndexedDB data is NEVER deleted — unlike
    // the message outbox below: queued punches stay on the device and resume
    // when the same account signs in again (Review Focus 3).
    stopOfflineAttendance();
    // Close the realtime socket BEFORE the tokens go: a frame or a pending
    // channel authorization that lands after this point must find no listener
    // and no bearer, or the previous user's events could reach the next
    // session's screen. (App.vue also stops it when `isAuthenticated` flips —
    // this direct call just removes the window between the two.) A company
    // switch also flows through here, so the socket, its cursor and its dedupe
    // window never outlive the tenant they were opened for. `forget: true`
    // removes the persisted cursor too (DEC-083) — the binding's key and,
    // defensively, every `syntry.realtime_cursor:` key.
    stopRealtime({ forget: true });
    // The queued message outbox of THIS session (every
    // `syntry.msg_outbox.v2:<companyKey>:<userId>:*` scope, plus every
    // identity-less v1 key) — keyed by the snapshot, never the token.
    useMessagesStore().clearOutboxForSession(getSessionScope());
    // A realtime target parked for this session must never replay for the
    // next account on this device.
    clearPendingAction();
    // The push registration + `invalid` records belong to this installation
    // AND this user; the endpoint itself was revoked in logout() when it could be.
    notificationsDevice.clearLocal();
    // The single sign-out choke point, so this is where the heartbeat stops for good — otherwise
    // a signed-out tab would keep reporting the previous user as online every 90 seconds.
    stopPresenceHeartbeat();
    // Same choke point for the reminder tick — a signed-out tab must not keep
    // refreshing the previous user's calendar.
    stopEventReminders();
    // Task ids repeat across tenants and users, so locally-held task fields
    // (estimate, collaborators, labels, focus) must be cleared while the token
    // can still tell us which user-owned storage key to remove.
    clearAllLocalTaskFields();
    // Same reason, and the same ordering constraint: the reminder plan is keyed
    // by tenant + user id, and it also disarms any device notification — which
    // would otherwise put an event title from an ended session on the lock
    // screen. Both need a decodable token, so this must precede clearSession().
    clearEventReminders();
    clearSession();
    // Same choke point for the analytics identity: from here on, events are
    // anonymous again. After clearSession() on purpose — its epoch bump is what
    // makes an identity still being hashed for the OLD session discard itself.
    clearAnalyticsUser();
    // The single sign-out choke point (manual logout, session expiry, …), so
    // this is where cached API reads for this user are dropped. Fire-and-forget
    // — sign-out must stay synchronous and instant.
    purgeUserCaches();
    // Same reasoning for the tenant's brand color: this is the one choke point
    // (manual logout, session expiry, company switch), so the next sign-in
    // screen is neutral Syntry teal rather than the previous tenant's color.
    // Deliberately NOT in applyTokenResponse() — that runs on every token
    // rotation and re-login, which would flash teal mid-session.
    resetBrandColor();
    useUserStore().clear();
    useMessagesStore().reset();
    useNotificationsStore().reset();
    // Cached colleague avatars are tenant-scoped — never carry them over.
    useAvatarsStore().reset();
    // Same for locally-planned "today" tasks — a task id from this tenant
    // means nothing (or the wrong thing) once attributed to another one.
    clearPlannedToday();
    // Locally-recorded note edit times are the same: tenant-scoped, and
    // meaningless (or wrong) once attributed to a different company's notes.
    clearNoteEdits();
    useSurveysStore().reset();
    // Cached colleague avatars are tenant-scoped — never carry them over.
    useAvatarsStore().reset();
    // Approval-queue access is a property of the PREVIOUS user's ERPat role.
    // Carrying it over would show the next account an approvals entry it has
    // no right to, so the next user must re-prove access against the server.
    useApprovalsStore().reset();
    // The Security PIN state and every unlocked confidential scope belong to the
    // PREVIOUS account. Carrying an unlock over would show the next user the
    // previous one's payslip figures without asking.
    useSecurityStore().reset();
    // The payroll cut-off calendar is scoped to BOTH the tenant and the employee — a department
    // can be assigned its own. Carrying it over would filter the next account's attendance by
    // the previous company's pay periods.
    useCutoffStore().reset();
    // Whether the Live Timelogs report is offered is the previous user's
    // permit, and its remembered filters list the previous tenant's employees
    // by name and code — neither may outlive the session, even unserved.
    forgetRealtimeLogsAvailability();
    // The clock state and the sync diagnostics on screen are the previous
    // account's. Only this VIEW of them goes; the punches stay on the device.
    useAttendanceStore().reset();
    useSyncStore().reset();
    resetMfa();
    status.value = 'guest';
    // LAST: every scoped clear above has read the snapshot; forget it now so a
    // guest tab holds no identity and the next sign-in captures its own.
    clearSessionScope();
  }

  return {
    status,
    mfaToken,
    isAuthenticated,
    mfaPending,
    login,
    completeMfa,
    restore,
    logout,
    signOutLocal,
    resetMfa,
    abandonSignIn,
  };
});
